import {
  RunCourseSlim,
  RunLineSlim,
  ReportRouteSlim,
  KilopostSlim,
} from '@/models/runCourse';
import { dtFormat, ensureDate } from '@/lib/dateHelper';
import {
  ROAD_NAME_SHORT_FORM,
  ROUTE_NAME_SHORT_FORM,
} from '@/consts/runCourse';
import { Kilopost } from '@/models/apis/device/adminDeviceResponse';

interface GeoKpSlim {
  kpDisp: string;
  routeNameShort: string;
  direction?: string;
}

function _getKpDisp(kp: KilopostSlim): string {
  if (kp.place_name === 'main_line') {
    return `${kp.road_name_short || kp.road_name_disp} (${kp.direction})`;
  }
  let placeName = kp.place_name;
  const m = placeName.match(/(^.+)JCT.*$/);
  if (m) {
    placeName = `${m[1]}JCT`;
  }
  const replaceMap = {
    '入口': 'on',
    '出口': 'off',
    'JCT': 'J',
    'J・': '',
    '（': '(',
    '）': ')',
  };
  Object.entries(replaceMap).forEach(e => {
    placeName = placeName.replaceAll(e[0], e[1]);
  });
  return placeName;
}

function _sortGeoKps(routeDisp: GeoKpSlim[]) {
  const directions = ['上', '下', '南', '北', '西', '東', '内', '外'];
  const routeNameShorts = Object.values(ROUTE_NAME_SHORT_FORM);
  routeDisp.sort((a, b) => {
    if (!a.direction) return 1;
    if (!b.direction) return -1;
    return directions.indexOf(a.direction) - directions.indexOf(b.direction);
  }).sort((a, b) => {
    if (routeNameShorts.indexOf(a.routeNameShort) < 0) return 1;
    if (routeNameShorts.indexOf(b.routeNameShort) < 0) return -1;
    return routeNameShorts.indexOf(a.routeNameShort) - routeNameShorts.indexOf(b.routeNameShort);
  });
}

function _formatStartEnd1(start: string, end: string): string {
  return [dtFormat(start, 'yyyy-mm-dd HH:MM:SS'), dtFormat(end, 'HH:MM:SS')].join('~');
}

const getKpDebugDisp = (runLine: RunLineSlim): string => {
  const kp1 = runLine.startKp;
  const kp2 = runLine.endKpBeforeChange;
  return `${kp1.road_name_disp}#${kp1.direction}#${kp1.place_name}#${kp1.kp_calc}~${kp2.kp_calc}`;
};

const getDurationDisp = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = (seconds % 3600) / 60;
  let ret = hours > 0 ? `${hours}時間` : '';
  ret += `${minutes.toFixed(1)}分`;
  return ret;
};

export function fillKpRoadNameShort(g1id: number, runCourse: RunCourseSlim): void {
  if (!runCourse) return;
  const roadNameShortForm = ROAD_NAME_SHORT_FORM[g1id];
  if (!roadNameShortForm) return;

  runCourse.routes
    .flatMap(route => route.lines)
    .flatMap(line => [line.startKp, line.endKpBeforeChange])
    .filter(Boolean)
    .forEach(kp => { kp.road_name_short = roadNameShortForm[kp.road_name_disp] ?? ''; });
}

export function getKpRoutes(
  g1id: number,
  roadNameDisps: string[],
  kps: Kilopost[],
): { [key: string]: { [key: string]: string } } {
  const roadNameShortForm = ROAD_NAME_SHORT_FORM[g1id];
  if (!roadNameShortForm) return {};

  const mainLineGeoKps: GeoKpSlim[] = [];
  const onGeoKps: GeoKpSlim[] = [];
  const offGeoKps: GeoKpSlim[] = [];
  const jctGeoKps: GeoKpSlim[] = [];
  kps.forEach(e => {
    const kp: KilopostSlim = {
      road_name_disp: e.road_name_disp,
      road_name_short: roadNameShortForm[e.road_name_disp],
      direction: e.direction,
      place_name: e.place_name,
      kp_calc: Number(e.kp_calc),
    };
    const kpDisp = _getKpDisp(kp);
    if (kp.place_name === 'main_line') {
      if (!mainLineGeoKps.find(x => x.kpDisp === kpDisp)) {
        mainLineGeoKps.push({ kpDisp, routeNameShort: ROUTE_NAME_SHORT_FORM[e.road_name], direction: e.direction });
      }
      return;
    }
    if (kpDisp.includes('on')) {
      if (!onGeoKps.find(x => x.kpDisp === kpDisp)) {
        onGeoKps.push({ kpDisp, routeNameShort: ROUTE_NAME_SHORT_FORM[e.road_name], direction: e.direction });
      }
      return;
    }
    if (kpDisp.includes('off')) {
      if (!offGeoKps.find(x => x.kpDisp === kpDisp)) {
        offGeoKps.push({ kpDisp, routeNameShort: ROUTE_NAME_SHORT_FORM[e.road_name], direction: e.direction });
      }
      return;
    }
    if (kpDisp.includes('J')) {
      if (!jctGeoKps.find(x => x.kpDisp === kpDisp)) {
        jctGeoKps.push({ kpDisp, routeNameShort: ROUTE_NAME_SHORT_FORM[e.road_name], direction: e.direction });
      }
    }
  });

  roadNameDisps.forEach(roadNameDisp => {
    let [routeName, direction] = roadNameDisp.split(' ');
    Object.entries({ '（': '(', '）': ')' }).forEach(e => {
      routeName = routeName.replaceAll(e[0], e[1]);
    });
    direction = direction?.replace(/\(|\)/g, '');
    if (roadNameDisp.includes('on')) {
      if (!onGeoKps.find(x => x.kpDisp === roadNameDisp)) {
        onGeoKps.push({ kpDisp: roadNameDisp, routeNameShort: ROUTE_NAME_SHORT_FORM[routeName], direction });
      }
      return;
    }
    if (roadNameDisp.includes('off')) {
      if (!offGeoKps.find(x => x.kpDisp === roadNameDisp)) {
        offGeoKps.push({ kpDisp: roadNameDisp, routeNameShort: ROUTE_NAME_SHORT_FORM[routeName], direction });
      }
      return;
    }
    if (roadNameDisp.includes('J')) {
      if (!jctGeoKps.find(x => x.kpDisp === roadNameDisp)) {
        jctGeoKps.push({ kpDisp: roadNameDisp, routeNameShort: ROUTE_NAME_SHORT_FORM[routeName], direction });
      }
      return;
    }
    if (!mainLineGeoKps.find(x => x.kpDisp === roadNameDisp)) {
      mainLineGeoKps.push({ kpDisp: roadNameDisp, routeNameShort: ROUTE_NAME_SHORT_FORM[routeName], direction });
    }
  });

  const mainLine: { [key: string]: string } = {};
  const on: { [key: string]: string } = {};
  const off: { [key: string]: string } = {};
  const jct: { [key: string]: string } = {};
  _sortGeoKps(mainLineGeoKps);
  mainLineGeoKps.forEach(e => {
    mainLine[e.kpDisp] = e.routeNameShort && e.direction ? `${e.routeNameShort} (${e.direction})` : e.kpDisp.replace(/on/, '');
  });
  _sortGeoKps(onGeoKps);
  onGeoKps.forEach(e => {
    on[e.kpDisp] = e.routeNameShort ? `${e.kpDisp.replace(/on/, '')} (${e.routeNameShort})` : e.kpDisp.replace(/on/, '');
  });
  _sortGeoKps(offGeoKps);
  offGeoKps.forEach(e => {
    off[e.kpDisp] = e.routeNameShort ? `${e.kpDisp.replace(/off/, '')} (${e.routeNameShort})` : e.kpDisp.replace(/off/, '');
  });
  _sortGeoKps(jctGeoKps);
  jctGeoKps.forEach(e => {
    jct[e.kpDisp] = e.routeNameShort ? `${e.kpDisp} (${e.routeNameShort})` : e.kpDisp;
  });

  return { 'main_line': mainLine, on, off, jct };
}

export function getDebugRunCourseInfo(runCourse: RunCourseSlim): string {
  if (!runCourse) return '';
  const result: string[] = [];
  runCourse.routes.forEach((runRoute, i) => {
    const firstRunLine = runRoute.lines[0];
    const lastRunLine = runRoute.lines.slice(-1)[0];
    const runRouteNumDisp = ('00' + (i + 1)).slice(-3);
    const firstRunLineStartTs = ensureDate(firstRunLine.startTs);
    const lastRunLineEndTs = ensureDate(lastRunLine.endTs);
    if (!firstRunLineStartTs || !lastRunLineEndTs) return;

    const durationSec = (lastRunLineEndTs.getTime() - firstRunLineStartTs.getTime()) / 1000;
    const runRouteTimeDisp = _formatStartEnd1(firstRunLine.startTs, lastRunLine.endTs);
    result.push(`Route ${runRouteNumDisp}, ${getDurationDisp(durationSec)} (${runRouteTimeDisp}):`);
    runRoute.lines.forEach((runLine, j) => {
      const arr = [
        _formatStartEnd1(runLine.startTs, runLine.endTs),
        `${_getKpDisp(runLine.startKp)}~ (${getKpDebugDisp(runLine)})`,
        `logic:${runLine.logic}`,
      ];
      const runLineNumDisp = ('00' + (j + 1)).slice(-3);
      result.push(`  ${runLineNumDisp}: ${arr.join(', ')}`);
    });
    result.push('');
  });
  return result.join('\n');
}

export function getReportRoutes(runCourse?: RunCourseSlim): ReportRouteSlim[][] {
  const routes = _getReportRouteSlims(runCourse);
  return routes.length > 0 ? [routes] : [];
}

export function _getReportRouteSlims(runCourse?: RunCourseSlim, breakType?: 'plus' | 'ellipsis'): ReportRouteSlim[] {
  const reportRoutes: ReportRouteSlim[] = [];
  if (!runCourse) return reportRoutes;

  runCourse.routes.forEach(route => {
    route.lines.forEach(line => {
      const lineStartTs = ensureDate(line.startTs);
      if (!lineStartTs) return;

      const startRoadNameDisp = _getKpDisp(line.startKp);
      const endBeforChangeRoadNameDisp = _getKpDisp(line.endKpBeforeChange);
      [startRoadNameDisp, endBeforChangeRoadNameDisp].forEach(e => {
        if (reportRoutes.length === 0 || reportRoutes.slice(-1)[0].startRoadNameDisp !== e) {
          reportRoutes.push({ startRoadNameDisp: e, startTs: dtFormat(lineStartTs), break: breakType });
        }
      });
    });
  });
  return reportRoutes;
}
